/**
 * `GameAction` — the closed, typed set of everything a key can be bound to (#64).
 *
 * This is the SINGLE source of truth for "what can a keybinding do". It is a
 * discriminated union so that adding a bindable action is a COMPILE ERROR
 * until every consumer (the `actionToIntent` mapper, `DEFAULT_KEYMAP`, and any
 * future customization UI) handles the new variant. That exhaustiveness is the
 * whole point: keybinding is data-driven, but the action space is closed and
 * type-checked.
 *
 * Two families:
 *   • Static actions — a fixed intent (`cycleEnemyTarget`, `clearHostile`).
 *     One variant each.
 *   • Indexed actions — parameterized by a bounded index. `castSlot` carries a
 *     branded `AbilitySlot`, so an out-of-range slot is UNREPRESENTABLE (the
 *     brand's smart constructor rejects it). This is why keybinds target ABILITY
 *     SLOTS, never ability identities — content decides what ability sits in a
 *     slot, so a config-authored champion needs zero keybinding code.
 *
 * #63 targeting (LANDED here): `cycleAllyTarget`, `clearFriendly`,
 *   `arenaTarget(index)`, `partyTarget(index)` are the friendly-slot mirror of
 *   the enemy cycle/clear plus the arena/party direct-select hotkeys. The
 *   exhaustive switch in `actionToIntent` (and `gameActionId` / `actionLabel` /
 *   the persist reviver) forces every one to be wired.
 *
 * #335: `stopCasting` is the client-action name for the sim's `stopCast` wire
 *   input (cast/channel cancel, #188/#185) — named distinctly from the wire
 *   message the same way `castSlot` (action) differs from `cast` (wire), so
 *   the action space and the protocol's message-kind vocabulary stay visibly
 *   separate. #563: the movement-only `stop` action (click-to-move's sibling)
 *   is REMOVED outright — WASD is the only movement input now, and a WASD
 *   directional release is driven by key-up transitions, not a bindable
 *   action.
 *
 * #815: `targetNearestEnemy` is the NEAREST-enemy selector Tab now defaults to.
 *   It is a NEW variant rather than a redefinition of `cycleEnemyTarget`:
 *   the seat-order cycle keeps its own action (and its own persisted id, so a
 *   stored keymap still parses), it just loses its default chord. An action's
 *   `kind` says what it does — two different selection rules are two actions.
 *
 * #622: `toggleAutoAttack` maps 1:1 to the sim's `toggleAutoAttack` wire
 *   input (flip `Entity.autoAttackEnabled`). Bound to spacebar by default,
 *   edge-triggered (auto-repeat swallowed, same as every other static action).
 *
 * #833: `openChat` is the FIRST action here that produces NO wire intent at
 *   all — it opens the in-game chat box, a purely client-local UI state
 *   change (`actionToIntent` returns no message for it, deliberately). It
 *   still lives in THIS union rather than as a hardcoded `Enter` listener so
 *   that (a) it is rebindable like everything else, and (b) it participates
 *   in `findConflicts`, so binding chat over an ability is reported rather
 *   than silently shadowing it. Defaults to `Enter`, which was unbound.
 *
 * FUTURE (additive, not a reshape):
 *   • #68 modifier chords change the KEY side (how an action is triggered), not
 *     the action side — this union is unaffected.
 */

import type { AbilitySlot } from "@pit/protocol";

/**
 * Upper bound (inclusive) on an arena/party direct-select index. The roster is
 * naturally bounded by the number of seats; this cap only exists to reject an
 * absurd hand-edited persisted keymap (mirrors the `MAX_ABILITY_SLOT` bound on
 * `castSlot`). 15 covers up to a 16-entity roster — well beyond MVP 1v1 and the
 * 3v3 roadmap. An in-range index that points at no seat is a runtime no-op, so
 * this is purely a persistence-boundary sanity limit, NOT a gameplay gate.
 */
export const MAX_TARGET_INDEX = 15 as const;

/**
 * Everything bindable. CLOSED discriminated union — a new action is a compile
 * error until the mapper's exhaustive switch handles it.
 *
 *   castSlot         — fire ability in `slot` (branded 0..MAX_ABILITY_SLOT).
 *                      Empty slots are inert (the sim ignores casts against
 *                      them) until content fills them.
 *   targetNearestEnemy — #815: acquire the NEAREST living enemy as the hostile
 *                      target, or do nothing when the current one is still
 *                      alive/targetable. Bound to Tab by default.
 *   cycleEnemyTarget — advance the hostile target to the NEXT enemy in seat
 *                      order (the pre-#815 Tab behavior). Still here, still
 *                      rebindable — but UNBOUND by default now that Tab means
 *                      "nearest"; bind it yourself for a cycle key.
 *   cycleAllyTarget  — friendly-slot mirror: advance the friendly target to the
 *                      next ally (self included, self-first roster order).
 *   clearHostile     — drop the hostile target slot.
 *   clearFriendly    — drop the friendly target slot.
 *   arenaTarget      — direct-select the `index`-th enemy into the hostile slot
 *                      (0-based; `arena1` key → index 0). NOT a cycle — a stable
 *                      roster slot regardless of the current target.
 *   partyTarget      — direct-select the `index`-th ally into the friendly slot
 *                      (0-based; `party1` key → index 0 = SELF).
 *   stopCasting      — immediately cancel an in-progress cast or channel
 *                      (#335). Maps to the `stopCast` wire input; a no-op when
 *                      idle (the sim's `breakCastOrChannel` short-circuits).
 *   toggleAutoAttack — flip the #93 auto-attack on/off switch (#622). Maps to
 *                      the `toggleAutoAttack` wire input; default ON, default
 *                      bound to spacebar.
 *   openChat         — open the in-game chat box (#833). CLIENT-LOCAL: it
 *                      produces no `SimInput` and never reaches the sim.
 *                      Default `Enter`; `Enter` again sends, `Escape`
 *                      cancels. While the box is open, gameplay keys are
 *                      captured (see `InputSurface`).
 */
export type GameAction =
  | { readonly kind: "castSlot"; readonly slot: AbilitySlot }
  | { readonly kind: "targetNearestEnemy" }
  | { readonly kind: "cycleEnemyTarget" }
  | { readonly kind: "cycleAllyTarget" }
  | { readonly kind: "clearHostile" }
  | { readonly kind: "clearFriendly" }
  | { readonly kind: "arenaTarget"; readonly index: number }
  | { readonly kind: "partyTarget"; readonly index: number }
  | { readonly kind: "stopCasting" }
  | { readonly kind: "toggleAutoAttack" }
  | { readonly kind: "openChat" };

/** Every `GameAction` discriminant. */
export type GameActionKind = GameAction["kind"];

/**
 * Stable string id for an action, used as the key when a keymap needs to
 * address an action (merge, dedupe, conflict reports). Total over the union —
 * a new variant that lacks an arm fails to compile via the `assertNever`
 * default.
 *
 * Indexed actions fold their index into the id (`castSlot:3`) so two different
 * slots are distinct actions. Static actions are their bare kind.
 */
export function gameActionId(action: GameAction): string {
  switch (action.kind) {
    case "castSlot":
      return `castSlot:${String(action.slot)}`;
    case "targetNearestEnemy":
      return "targetNearestEnemy";
    case "cycleEnemyTarget":
      return "cycleEnemyTarget";
    case "cycleAllyTarget":
      return "cycleAllyTarget";
    case "clearHostile":
      return "clearHostile";
    case "clearFriendly":
      return "clearFriendly";
    case "arenaTarget":
      return `arenaTarget:${String(action.index)}`;
    case "partyTarget":
      return `partyTarget:${String(action.index)}`;
    case "stopCasting":
      return "stopCasting";
    case "toggleAutoAttack":
      return "toggleAutoAttack";
    case "openChat":
      return "openChat";
    default:
      return assertNever(action);
  }
}

/** Structural equality for two actions (by their canonical id). */
export function gameActionsEqual(a: GameAction, b: GameAction): boolean {
  return gameActionId(a) === gameActionId(b);
}

/**
 * Exhaustiveness guard. Reaching this at runtime is impossible for a
 * well-typed union; the `never` parameter makes an unhandled variant a
 * COMPILE error at every call site.
 */
function assertNever(x: never): never {
  throw new Error(`unhandled GameAction: ${JSON.stringify(x)}`);
}
